import {
  createKucoinDepositHistoryData,
  createKucoinSpotTradeHistoryData,
  createKucoinWithdrawalHistoryData,
} from '@0xtorch/exchange'
import {
  type AccountExchangeApi,
  saveExchangeActions,
} from '@pkg/database-portfolio'
import { accountExchangeApiTable } from '@pkg/database-portfolio/src/schema'
import { conflictUpdateAllExcept } from '@pkg/database-portfolio/src/utils'
import type { RunToDatabase, SaveExchangeApiDataParameters } from './types'

const exchangeStartDate = new Date('2017-09-01')

const days180Milliseconds = 180 * 24 * 60 * 60 * 1000

export const saveKucoinApiData = async (
  params: SaveExchangeApiDataParameters,
): Promise<void> => {
  const { account, apiId } = params

  if (
    account.exchangeApiKey === undefined ||
    account.exchangeApiSecret === undefined ||
    account.exchangeApiPassword === undefined
  ) {
    throw new Error(`api keys are undefined of '${account.id}'`)
  }

  // api 毎に処理
  switch (apiId) {
    case 'deposit': {
      await saveDepositData(params)
      break
    }
    case 'withdrawal': {
      await saveWithdrawalData(params)
      break
    }
    case 'spot-trade': {
      await saveSpotTradeData(params)
      break
    }
    default: {
      throw new Error(`Unknown bitget apiId: ${apiId}`)
    }
  }
}

const saveApiData = async (params: {
  writeToDatabase: RunToDatabase
  getToken: (() => Promise<string | null>) | undefined
  startTime: number
  accountApi: AccountExchangeApi
  call: (params: { from: number; to: number; token?: string }) => ReturnType<
    typeof createKucoinDepositHistoryData
  >
  maxTimeRange?: number
}) => {
  const {
    writeToDatabase,
    getToken,
    startTime,
    accountApi,
    call,
    maxTimeRange,
  } = params

  const now = Date.now()

  // 最終同期から 1 時間未満だったらスキップ
  if (accountApi.to.getTime() > now - 60 * 60 * 1000) {
    return
  }

  // desc で取得
  // maxTimeRange が指定されている場合はその期間内で取得
  const start = Math.max(startTime, accountApi.to.getTime() + 1)
  let latest = 0
  let to = now
  let from =
    maxTimeRange === undefined ? start : Math.max(start, to - maxTimeRange)
  while (to > from) {
    const token =
      getToken === undefined ? undefined : ((await getToken()) ?? undefined)
    const {
      actions,
      sources,
      desc,
      from: fetchedFrom,
      to: fetchedTo,
    } = await call({ from, to, token })

    // データの保存処理
    await writeToDatabase((database) =>
      saveExchangeActions({
        database,
        accountId: accountApi.accountId,
        actions: actions.filter(({ timestamp }) => timestamp >= startTime),
        transferSources: sources,
      }),
    )

    // 連続で呼びすぎないように待機
    await new Promise((resolve) => setTimeout(resolve, 100))

    // データがまだあれば次のデータを取得
    // desc によって次のデータ取得期間を調整する
    if (desc !== true) {
      throw new Error(`Unknown desc: ${desc}`)
    }
    if (fetchedTo !== undefined) {
      latest = Math.max(latest, fetchedTo)
    }
    to =
      fetchedFrom === undefined || fetchedFrom === fetchedTo
        ? from - 1
        : fetchedFrom
    from =
      maxTimeRange === undefined ? start : Math.max(start, to - maxTimeRange)
  }

  // accountApi の更新
  // now の一分前と最新の取引時間の新しい方を to にする
  const toTime = Math.max(latest, now - 60_000)
  await writeToDatabase((database) =>
    database.database
      .insert(accountExchangeApiTable)
      .values({
        ...accountApi,
        to: new Date(toTime),
      })
      .onConflictDoUpdate({
        target: [
          accountExchangeApiTable.accountId,
          accountExchangeApiTable.apiId,
        ],
        set: conflictUpdateAllExcept(accountExchangeApiTable, [
          'accountId',
          'apiId',
        ]),
      }),
  )
}

const saveDepositData = async ({
  writeToDatabase,
  getToken,
  startTime,
  account,
  accountApis,
  proxyApiEndpoint,
  cryptoCurrencyList,
  fiatCurrency,
  fiatCurrencyList,
  cryptoCurrencyDataSource,
}: SaveExchangeApiDataParameters) => {
  if (
    account.exchangeApiKey === undefined ||
    account.exchangeApiSecret === undefined ||
    account.exchangeApiPassword === undefined
  ) {
    throw new Error(`api keys are undefined of '${account.id}'`)
  }
  const apiKey = account.exchangeApiKey
  const apiSecret = account.exchangeApiSecret
  const password = account.exchangeApiPassword

  const apiId = 'deposit'
  const accountApi = accountApis.find(
    (accountApi) => accountApi.apiId === apiId,
  ) ?? {
    accountId: account.id,
    apiId,
    from: exchangeStartDate,
    to: exchangeStartDate,
  }

  // 最終同期から 1 時間未満だったらスキップ
  if (accountApi.to.getTime() > Date.now() - 60 * 60 * 1000) {
    return
  }

  await saveApiData({
    writeToDatabase,
    getToken,
    accountApi,
    startTime,
    call: ({ from, to, token }) =>
      createKucoinDepositHistoryData({
        apiKey,
        apiSecret,
        password,
        from,
        to,
        proxy: proxyApiEndpoint,
        proxyHeaders:
          token === undefined
            ? undefined
            : {
                Authorization: `Bearer ${token}`,
              },
        accountId: account.id.toString(),
        cryptoCurrencies: cryptoCurrencyList,
        fiatCurrencies: fiatCurrencyList,
        fiat: fiatCurrency,
        cryptoCurrencyDataSource: cryptoCurrencyDataSource,
      }),
  })
}

const saveSpotTradeData = async ({
  writeToDatabase,
  getToken,
  startTime,
  account,
  accountApis,
  proxyApiEndpoint,
  cryptoCurrencyList,
  fiatCurrency,
  fiatCurrencyList,
  cryptoCurrencyDataSource,
}: SaveExchangeApiDataParameters) => {
  if (
    account.exchangeApiKey === undefined ||
    account.exchangeApiSecret === undefined ||
    account.exchangeApiPassword === undefined
  ) {
    throw new Error(`api keys are undefined of '${account.id}'`)
  }
  const apiKey = account.exchangeApiKey
  const apiSecret = account.exchangeApiSecret
  const password = account.exchangeApiPassword

  const apiId = 'spot-trade'
  // 最大で 180 日前から取得
  const timestampDays180Ago = Date.now() - days180Milliseconds
  const startDate = new Date(
    Math.max(exchangeStartDate.getTime(), timestampDays180Ago),
  )
  const accountApi = accountApis.find(
    (accountApi) => accountApi.apiId === apiId,
  ) ?? {
    accountId: account.id,
    apiId,
    from: startDate,
    to: startDate,
  }
  if (accountApi.to.getTime() < timestampDays180Ago) {
    accountApi.to = new Date(timestampDays180Ago)
  }

  // 最終同期から 1 時間未満だったらスキップ
  if (accountApi.to.getTime() > Date.now() - 60 * 60 * 1000) {
    return
  }

  await saveApiData({
    writeToDatabase,
    getToken,
    accountApi,
    startTime,
    call: ({ from, to, token }) =>
      createKucoinSpotTradeHistoryData({
        apiKey,
        apiSecret,
        password,
        from,
        to,
        proxy: proxyApiEndpoint,
        proxyHeaders:
          token === undefined
            ? undefined
            : {
                Authorization: `Bearer ${token}`,
              },
        accountId: account.id.toString(),
        cryptoCurrencies: cryptoCurrencyList,
        fiatCurrencies: fiatCurrencyList,
        fiat: fiatCurrency,
        cryptoCurrencyDataSource: cryptoCurrencyDataSource,
      }),
    // 7 days
    maxTimeRange: 1000 * 60 * 60 * 24 * 7,
  })
}

const saveWithdrawalData = async ({
  writeToDatabase,
  getToken,
  startTime,
  account,
  accountApis,
  proxyApiEndpoint,
  cryptoCurrencyList,
  fiatCurrency,
  fiatCurrencyList,
  cryptoCurrencyDataSource,
}: SaveExchangeApiDataParameters) => {
  if (
    account.exchangeApiKey === undefined ||
    account.exchangeApiSecret === undefined ||
    account.exchangeApiPassword === undefined
  ) {
    throw new Error(`api keys are undefined of '${account.id}'`)
  }
  const apiKey = account.exchangeApiKey
  const apiSecret = account.exchangeApiSecret
  const password = account.exchangeApiPassword

  const apiId = 'withdrawal'
  const accountApi = accountApis.find(
    (accountApi) => accountApi.apiId === apiId,
  ) ?? {
    accountId: account.id,
    apiId,
    from: exchangeStartDate,
    to: exchangeStartDate,
  }

  // 最終同期から 1 時間未満だったらスキップ
  if (accountApi.to.getTime() > Date.now() - 60 * 60 * 1000) {
    return
  }

  await saveApiData({
    writeToDatabase,
    getToken,
    accountApi,
    startTime,
    call: ({ from, to, token }) =>
      createKucoinWithdrawalHistoryData({
        apiKey,
        apiSecret,
        password,
        from,
        to,
        proxy: proxyApiEndpoint,
        proxyHeaders:
          token === undefined
            ? undefined
            : {
                Authorization: `Bearer ${token}`,
              },
        accountId: account.id.toString(),
        cryptoCurrencies: cryptoCurrencyList,
        fiatCurrencies: fiatCurrencyList,
        fiat: fiatCurrency,
        cryptoCurrencyDataSource: cryptoCurrencyDataSource,
      }),
  })
}
